import React, { useState } from 'react';
import { getRecordingUrl } from '../../../../../services/recordingService';

/**
 * RecordingPlayer
 *
 * SUPER_ADMIN-only control for playing a call recording. On click it fetches a
 * short-lived signed URL from the `get-recording` edge function and plays it in
 * an inline <audio> element. Visibility gating (role + recording_url presence)
 * is the caller's responsibility.
 *
 * Props:
 *   - callId:  the effective call id — a Vapi call uuid (call.call_id) or a
 *              Retell call id (call.provider_call_id) when call_id is NULL
 *   - variant: 'icon' (compact 🎵, default) | 'button' (labeled button)
 */
const RecordingPlayer = ({ callId, variant = 'icon' }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLoad = async () => {
    setLoading(true);
    setError(null);
    try {
      // Always fetch fresh — signed URLs are short-lived.
      const signedUrl = await getRecordingUrl(callId);
      setUrl(signedUrl);
    } catch (err) {
      setError(err.message || 'Could not load the recording.');
    } finally {
      setLoading(false);
    }
  };

  if (url) {
    return <audio controls autoPlay src={url} className="max-w-full" />;
  }

  const isButton = variant === 'button';
  const baseClass = isButton
    ? 'px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50'
    : 'text-purple-600 hover:text-purple-900 disabled:opacity-50';

  const label = isButton
    ? (loading ? '🎵 Loading…' : '🎵 Play Recording')
    : (loading ? '⏳' : '🎵');

  return (
    <span className={isButton ? 'inline-flex flex-col items-end' : 'inline-flex flex-col items-center'}>
      <button
        type="button"
        onClick={handleLoad}
        disabled={loading}
        className={baseClass}
        title="Play Recording"
      >
        {label}
      </button>
      {error && (
        <span className="mt-1 text-xs text-red-600" title={error}>
          {error}
        </span>
      )}
    </span>
  );
};

export default RecordingPlayer;
